import { Tooltip, Position, Icon } from '@blueprintjs/core';
import { Intent } from '@blueprintjs/core/lib/esm/common/intent';
import axios from 'axios';
import { Button } from 'components/buttons';
import { FormNumericInput, FormRadio, FormSelect } from "components/form-fields";
import { ShowHide } from "components/layout";
import { ListingTable } from "components/listing";
import { NotificationInline, NotificationToaster } from "components/notifications";
import { useDirtyData } from 'hooks/useDirtyData';
import { useValidation } from 'hooks/useValidation';
import { useEventWizard } from 'pages/asset/events/shared/EventWizardContext';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FleetService } from "services";
import { v4 as uuidv4 } from 'uuid';
import { Money } from "components/formatting/Money";
import { EventAddLaterOptions } from 'pages/asset/events/modals/DefaultModels';

const COST_ITEMS_LIMIT = 50;

export function CostEvent({}) {

    const { 
        event, 
        eventType, 
        saving, 
        loadingData, 
        stepsConfig, 
        tempCostList, 
        setStepsConfig, 
        setStepIsValid, 
        setTempCostList, 
        setEvent,
        setHasMadeChanges,
        setActiveStep,
        setCanSave
    } = useEventWizard();

    const isMountedRef = React.useRef(false);

    const isStepDataDirty = useDirtyData({
        costs: event?.costs ?? [],
    }, {
        costs: tempCostList,
    });

    const [isValid, errors, validate] = useValidation(true, true);

    const config = stepsConfig.CostEvent;
    const { addCostLater } = config;

    const [isLoadingCost, setIsLoadingCost] = useState(false);
    const [costType, setCostType] = useState([]);
    const [supplierType, setSupplierType] = useState([]);

    const isDisabled = saving || loadingData;

    const tempCostListWithTotal = useMemo(() => {
        return tempCostList.reduce((acc, curr) => {
            const totalCost = (parseFloat(curr.labour) || 0) + (parseFloat(curr.parts) || 0) + (parseFloat(curr.sundries) || 0);
            return [
                ...acc,
                {
                    ...curr,
                    totalCost
                }
            ];
        }, []);
    }, [tempCostList]);

    useEffect(() => {
        setActiveStep("Cost");
    }, []);

    const refreshEventData = () => {
        FleetService.getEvent(event.eventId).then((getEventTypeResponse) => {
            setEvent(getEventTypeResponse);
            setTempCostList(getEventTypeResponse.costs || []);
            setIsLoadingCost(false);
        }).catch(() => {
            NotificationToaster.show(Intent.DANGER, "Failed to refresh modal. Please close and try again.");
        }).finally(() => {
            setIsLoadingCost(false);
        });
    }

    useEffect(() => {
        setCanSave(isStepDataDirty && !isLoadingCost && isValid);
    }, [isStepDataDirty, isLoadingCost, isValid]);

    config.onSave = () => {
        let promises = [];
        let costsToCreate = {
            eventCosts: tempCostListWithTotal.filter(i => !i.eventCostId)
        };
        let costsToUpdate = {
            eventCosts: tempCostListWithTotal.filter(i => i.eventCostId)
        };

        if (costsToCreate?.eventCosts?.length > 0) {
            promises.push(FleetService.createEventCost(event.eventId, costsToCreate));
        }

        if (costsToUpdate?.eventCosts?.length > 0) {
            promises.push(FleetService.updateEventCost(event.eventId, costsToUpdate));
        }

        if (promises.length === 0) {
            return;
        }

        setIsLoadingCost(true);

        axios.all(promises).then(axios.spread(() => {
            setHasMadeChanges(true);
            //We'll refresh to rest the dirty flag
            refreshEventData();
            NotificationToaster.show(Intent.SUCCESS, "Cost item updated successfully.");
        }), () => {
            NotificationToaster.show(Intent.DANGER, "Failed to update the cost item. Please try again.");
            setIsLoadingCost(false);
        });
    }

    const deleteCostItem = (id) => {
        setIsLoadingCost(true);

        FleetService.deleteEventCost(event?.eventId, id)
            .then(() => {
                setHasMadeChanges(true);
                refreshEventData();
                NotificationToaster.show(Intent.SUCCESS, "Cost item deleted successfully.");
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Failed to delete the cost item. Please try again.");
                setIsLoadingCost(true);
            });
    }

    const onAdditionalCostAddSelect = (data) => {
        if (!data.costTypeId) {
            return;
        }

        //Lets set up the full cost item with default values
        setTempCostList(prev => ([
            ...prev,
            {
                tempId: uuidv4(),
                costTypeId: data.costTypeId,
                labour: 0,
                parts: 0,
                sundries: 0
            },
        ]));
    }

    const onUpdateCostItem = (id, data) => {
        setTempCostList(prevState => prevState.map(item => {
            if (item.tempId === id || item.eventCostId === id) {
                return {
                    ...item,
                    ...data
                };
            }
            return item;
        }));
    };

    const onDeleteCostItem = (id) => {

        const item = tempCostList.find(i => i.tempId === id || i.eventCostId === id);
        const isTempItem = !!item?.tempId;

        if (isTempItem) {
            setTempCostList(prevState => prevState.filter(i => i.tempId !== id));
        } else {
            setTempCostList(prevState => prevState.filter(i => i.eventCostId !== id));
            deleteCostItem(id);
        }
    };

    function onRadioFieldChange(item) {
        let addCostLaterValue = item.currentTarget.value === "true";
        setStepsConfig(prev => ({
            ...prev,
            CostEvent: {
                ...prev.CostEvent,
                addCostLater: addCostLaterValue
            },
            CostDocumentEvent: {
                ...prev.CostDocumentEvent,
                addCostDocumentsLater: addCostLaterValue
            }
        }));
    }

    const handleInputChange = (itemId, data) => {
        if (itemId === -1) {
            onAdditionalCostAddSelect(data);
        } else {
            onUpdateCostItem(itemId, data);
        }
    }

    const validator = () => {
        const rules = [
            {
                fieldName: "labour",
                minValue: 0,
            },
            {
                fieldName: "parts",
                minValue: 0,
            },
            {
                fieldName: "sundries",
                minValue: 0,
            },
            {
                fieldName: "totalCost",
                minValue: 0.009,
            },
            {
                fieldName: "supplierId",
                required: eventType?.requiresSuppliers
            }
        ];

        // We have set up useValidation to expect an array of items to validate
        validate(rules, tempCostListWithTotal);
    };

    const ErrorIcon = useCallback(({ error }) => {
        if (!error) {
            return null;
        }
        return (
            <Tooltip content={error} position={Position.TOP} >
                <Icon className="text-intent-danger" icon="warning-sign" />
            </Tooltip>
        )
    }, []);

    const costTypeHasLinkedSupplier = useCallback((costTypeId) => {
        return supplierType.some(s => (s.relatedCosts ?? []).some(rc => rc.id === costTypeId));
    }, [supplierType]);

    const costTypeItems = useMemo(() => {

        if (!eventType?.requiresSuppliers) {
            return costType;
        }

        // Cost types with no linked supplier can never satisfy the required-supplier rule,
        // so disable them in the dropdown to stop new costs entering an unsaveable state
        return costType.map((ct) => {
            if (costTypeHasLinkedSupplier(ct.id)) {
                return ct;
            }

            return {
                ...ct,
                disabled: true,
                label: "No supplier linked"
            };
        });
    }, [costType, costTypeHasLinkedSupplier, eventType]);

    const hasCostsWithoutLinkableSupplier = useMemo(() => {

        if (!eventType?.requiresSuppliers) {
            return false;
        }

        return tempCostList.some((c) => c.costTypeId && !costTypeHasLinkedSupplier(c.costTypeId));
    }, [tempCostList, costTypeHasLinkedSupplier, eventType]);

    const costEventHeaders = useMemo(() => {

        const headers = ["Name", "Labour", "Parts", "Sundries", "Total"];

        if (eventType?.includeSuppliers) {
            headers.push("Supplier");
        }

        if (tempCostList?.length > 0 && !isDisabled) {
            headers.push("Delete");
        }

        return headers;
    }, [tempCostList, eventType, isDisabled]);

    const costEventsListingData = useMemo(() => {

        const list = [
            ...tempCostListWithTotal,
        ];

        if (!isDisabled && list.length < COST_ITEMS_LIMIT) {
            list.push({
                tempId: -1,
            });
        }

        return list.map((ec, index) => {
            const itemId = ec.eventCostId ?? ec.tempId;

            let rowSupplierType = [];

            if (ec.costTypeId && supplierType && supplierType.length > 0) {
                rowSupplierType = supplierType.filter(s => s.relatedCosts.map((rc) => { return rc.id }).includes(ec.costTypeId));
            }

            const inputs = [
                <div key={`cost-type-select-${itemId}`} className='inline-flex-center'>
                    <FormSelect
                        items={costTypeItems}
                        selectedValue={ec.costTypeId}
                        onItemSelect={(s) => handleInputChange(itemId, { costTypeId: s.id })}
                        placeholder="Please select to create a new cost"
                        disabled={itemId === -1 && !isValid && list.length > 1}
                        small
                    ></FormSelect>
                    <ErrorIcon error={(itemId === -1 && config.costIsRequired && list.length === 1) ? "Costs are required" : null} />
                </div>,
                <div key={`cost-labor-${itemId}`} className='inline-flex-center'>
                    <FormNumericInput
                        min={0}
                        max={999999999}
                        allowDecimal
                        selectedValue={ec.labour || 0}
                        onValueChange={(value) => handleInputChange(itemId, { labour: value })}
                        disabled={itemId === -1}
                        disabledInput={itemId === -1}
                    />
                    <ErrorIcon error={itemId !== -1 && errors[index]?.labour} />
                </div>,
                <div key={`cost-parts-${itemId}`} className='inline-flex-center'>
                    <FormNumericInput
                        min={0}
                        max={999999999}
                        allowDecimal
                        selectedValue={ec.parts || 0}
                        onValueChange={(value) => handleInputChange(itemId, { parts: value })}
                        disabled={itemId === -1}
                        disabledInput={itemId === -1}
                    />
                    <ErrorIcon error={itemId !== -1 && errors[index]?.parts} />
                </div>,
                <div key={`cost-sundries-${itemId}`} className='inline-flex-center'>
                    <FormNumericInput
                        min={0}
                        max={999999999}
                        allowDecimal
                        selectedValue={ec.sundries || 0}
                        onValueChange={(value) => handleInputChange(itemId, { sundries: value })}
                        disabled={itemId === -1}
                        disabledInput={itemId === -1}
                    />
                    <ErrorIcon error={itemId !== -1 && errors[index]?.sundries} />
                </div>,
                <div key={`cost-total-${itemId}`} className='inline-flex-center'>
                    <Money>{ec.totalCost ?? 0}</Money>
                    <ErrorIcon error={itemId !== -1 && errors[index]?.totalCost} />
                </div>
            ];

            if (itemId !== -1 && eventType?.includeSuppliers) {
                if (rowSupplierType.length > 0) {
                    inputs.push(
                        <div key={`cost-supplier-select-${itemId}`} className='inline-flex-center'>
                            <FormSelect
                                items={rowSupplierType}
                                selectedValue={ec.supplierId}
                                onItemSelect={(s) => handleInputChange(itemId, { supplierId: s.id })}
                                placeholder="Please select a supplier"
                                small
                                disabled={itemId === -1}
                            ></FormSelect>
                            <ErrorIcon error={itemId !== -1 && errors[index]?.supplierId} />
                        </div>
                    );
                } else {
                    inputs.push(
                        <div key={`cost-supplier-n/a-${itemId}`} className='inline-flex-center'>
                            N/A
                            <ErrorIcon error={eventType?.requiresSuppliers ? "A supplier is required, but no suppliers are linked to this cost type." : null} />
                        </div>
                    );
                }
                
            }

            if (itemId !== -1 && !isDisabled) {
                inputs.push(
                    <div key={`cost-delete-${itemId}`} className='inline-flex-center'>
                        <Button iconOnly icon="trash" minimal large={false} onClick={() => onDeleteCostItem(itemId)} />
                    </div>
                );
            } 
            // If we don't have any actions then ensure an empty div is pushed to align the rows columns with the headers
            else {
                inputs.push(
                    <div className='inline-flex-center'></div>
                );
            }

            return inputs;
        });
    }, [costTypeItems, tempCostListWithTotal, eventType, supplierType, isDisabled, errors, isValid]);

    // Load Dependency Data
    useEffect(() => {
        setIsLoadingCost(true);

        const costsRequest = {
            requestCount: 1000,
            pageNumber: 1,
            searchTerm: "",
            filters: [{ key: "CategoryName", value: "asset" }]
        };

        const requestChain = [
            FleetService.getCosts(costsRequest)
        ]

        if (eventType?.includeSuppliers) {
            requestChain.push(FleetService.searchSuppliers(1000, 1, "", "name", "asc"));
        }

        axios.all(requestChain)
            .then(axios.spread(function (...response) {

                const getCostsResponse = response[0];
                const getSuppliersResponse = response[1];

                setCostType(getCostsResponse.data);
                getSuppliersResponse && setSupplierType(getSuppliersResponse.data);
            }), () => {
                NotificationToaster.show(Intent.DANGER, "Could not load the cost data needed. Please refresh the page.");
            })
            .finally(() => setIsLoadingCost(false));
    }, []);

    // Load Step Data
    useEffect(() => {
        if (isMountedRef.current || tempCostList.length > 0) {
            return;
        }

        const eventCosts = event?.costs || [];
        eventCosts.length && setTempCostList(eventCosts);
        isMountedRef.current = true;
    }, [event]);

    useEffect(() => {
        setStepIsValid(!isStepDataDirty && (!config.costIsRequired || addCostLater || event.costs.length > 0));
    }, [isStepDataDirty, tempCostList, config.costIsRequired, addCostLater, event]);

    useEffect(() => {
        isStepDataDirty && validator();
    }, [isStepDataDirty, tempCostList]);

    return (
        <div>
            <h2>Cost Event</h2>

            <NotificationInline
                id="notification-event-helper"
                loading={loadingData}
                show={eventType?.costHelperText && eventType.costHelperText.length > 0}
                text={eventType?.costHelperText}
                allowClose={false} />

            <NotificationInline
                id="notification-cost-event-"
                intent={Intent.WARNING}
                loadingData={loadingData || isLoadingCost}
                show={isStepDataDirty && !isLoadingCost}
                text="There are unsaved changes in this step."
                allowClose={false}
            />

            <NotificationInline
                id="notification-cost-supplier-required"
                intent={Intent.WARNING}
                show={hasCostsWithoutLinkableSupplier && !isLoadingCost}
                text="One or more costs require a supplier, but no suppliers are linked to their cost type. Link a supplier to the cost type in Fleet Settings, or remove the cost, to enable saving."
                allowClose={false}
            />

            <ShowHide
                evaluator={config.costIsRequired && (tempCostList?.length ?? 0) === 0}
                show={
                    <FormRadio
                        disabled={saving}
                        options={EventAddLaterOptions}
                        onChange={(item) => onRadioFieldChange(item)}
                        headingText="Add cost(s) later"
                        selectedValue={addCostLater}
                        loading={loadingData}
                        id="input-add-costs-later"
                    />
                }
            />

            <ShowHide
                evaluator={!addCostLater}
                show={
                    <ListingTable
                        id="cost-event-listing"
                        headers={costEventHeaders}
                        data={costEventsListingData}
                        totalRecordCount={costEventsListingData.length}
                        pageable={false}
                        loadingData={isLoadingCost || loadingData}
                    />
                }
            />
        </div>
    );
}

CostEvent.propTypes = {
};